import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { publicRessourcesAPI } from '../api/api';
import {
  ArticleRessource, BadgeNouveau, EnTetePartage, IconeLienExterne, PageChargement, PageLienInvalide, TagsDomaines,
  dateRessource, extrait, fmtDate, majuscule, sansAccents, sourceRessource,
} from '../components/RessourcePartage';

// Thèmes affichés avant « Autres thèmes » (les plus fréquents d'abord) ; moins sur mobile
const NB_THEMES_VISIBLES = 8;
const NB_THEMES_MOBILE = 3;

const moisDe = (d) => majuscule(d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }));

// Ressource avec un texte (ouvre la lecture) ou un lien (ouvre le site) : toute la carte est cliquable
function CarteRessource({ r, onLire }) {
  const source = sourceRessource(r);
  const classeTitre = 'text-left text-base sm:text-lg font-semibold leading-snug text-gray-900 group-hover:text-blue-700 focus:outline-none after:absolute after:inset-0';

  return (
    <article className="group relative rounded-lg border border-gray-200 bg-white p-4 sm:p-5 transition hover:border-gray-300 hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 mb-1.5">
        {r.est_nouveau && <BadgeNouveau />}
        <span>{fmtDate(r._date)}</span>
        {source && <span>· {source}</span>}
      </div>
      <h3>
        {r.notes
          ? <button type="button" onClick={() => onLire(r.id)} className={classeTitre}>{r.nom}</button>
          : <a href={r.url} target="_blank" rel="noopener noreferrer" className={classeTitre}>{r.nom}</a>}
      </h3>
      {r.notes && <p className="mt-1.5 text-sm leading-relaxed text-gray-600 line-clamp-2">{extrait(r)}</p>}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <TagsDomaines domaines={r.domaines} />
        <span className="ml-auto inline-flex items-center gap-1 text-sm font-medium text-blue-700">
          {r.notes
            ? <>Lire <span aria-hidden="true">→</span></>
            : <>Ouvrir {source || 'le lien'} <IconeLienExterne className="w-3.5 h-3.5" /></>}
        </span>
      </div>
    </article>
  );
}

// Sujet noté sans texte ni lien (anciennes fiches) : simple ligne, rien à ouvrir
const LigneSujet = ({ r }) => (
  <li className="flex flex-wrap items-baseline gap-x-3 gap-y-1 py-2">
    <span className="w-16 shrink-0 text-xs text-gray-400">{r._date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
    <span className="text-sm text-gray-700">{r.nom}</span>
  </li>
);

function PanneauLecture({ ressource, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-label={ressource.nom}>
      <div className="absolute inset-0 bg-gray-900/30" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 sm:px-8 py-3">
          <button type="button" onClick={onClose} autoFocus
            className="inline-flex items-center gap-1.5 rounded text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
            <span aria-hidden="true">←</span> Retour à la liste
          </button>
          <button type="button" onClick={onClose} aria-label="Fermer" className="p-1 text-gray-400 hover:text-gray-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6">
          <ArticleRessource ressource={ressource} Titre="h2" />
        </div>
      </div>
    </div>
  );
}

const ChipTheme = ({ actif, onClick, children, className = '' }) => (
  <button type="button" onClick={onClick} aria-pressed={actif}
    className={`${className} rounded-full border px-3 py-1 text-sm transition ${actif
      ? 'border-gray-900 bg-gray-900 text-white'
      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50'}`}>
    {children}
  </button>
);

export default function RessourcesPubliques() {
  const { token } = useParams();
  const [ressources, setRessources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [search, setSearch] = useState('');
  const [domaine, setDomaine] = useState('');
  const [nouveautes, setNouveautes] = useState(false);
  const [tousThemes, setTousThemes] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const fermer = useCallback(() => setDetailId(null), []);

  useEffect(() => { document.title = 'Ressources documentaires · AXELERANCE'; }, []);

  useEffect(() => {
    publicRessourcesAPI.getAll(token)
      .then(data => setRessources(Array.isArray(data) ? data : []))
      .catch(() => setInvalid(true))
      .finally(() => setLoading(false));
  }, [token]);

  // Du plus récent au plus ancien, selon la date affichée (et non la date de saisie)
  const triees = useMemo(() => ressources
    .map(r => ({ ...r, _date: dateRessource(r), _texte: sansAccents([r.nom, r.notes, ...(r.domaines || [])].join(' ')) }))
    .sort((a, b) => b._date - a._date), [ressources]);

  const themes = useMemo(() => {
    const n = {};
    ressources.forEach(r => (r.domaines || []).forEach(d => { n[d] = (n[d] || 0) + 1; }));
    return Object.entries(n).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'fr'));
  }, [ressources]);

  const nbNouveaux = ressources.filter(r => r.est_nouveau).length;
  const mots = sansAccents(search).split(/\s+/).filter(Boolean);
  const filtered = triees.filter(r =>
    (!nouveautes || r.est_nouveau)
    && (!domaine || (r.domaines || []).includes(domaine))
    && mots.every(m => r._texte.includes(m)));

  // Regroupement par mois ; dans un mois, les ressources lisibles d'abord, puis les sujets sans contenu
  const groupes = [];
  filtered.forEach(r => {
    const cle = `${r._date.getFullYear()}-${r._date.getMonth()}`;
    let g = groupes[groupes.length - 1];
    if (!g || g.cle !== cle) groupes.push(g = { cle, titre: moisDe(r._date), cartes: [], sujets: [] });
    (r.notes || r.url ? g.cartes : g.sujets).push(r);
  });

  const filtresActifs = search || domaine || nouveautes;
  const effacer = () => { setSearch(''); setDomaine(''); setNouveautes(false); };
  const detailRessource = ressources.find(r => r.id === detailId) || null;

  if (loading) return <PageChargement />;
  if (invalid) return <PageLienInvalide />;

  return (
    <div className="min-h-screen bg-gray-50">
      <EnTetePartage />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 mb-3">Ressources documentaires</h1>
        <p className="max-w-2xl text-base leading-relaxed text-gray-600 mb-8">
          La veille documentaire d'AXELERANCE pour ses formateurs : articles, textes officiels et décryptages
          repérés au fil de l'actualité, pour vous tenir à jour et nourrir vos formations. Les plus récents
          apparaissent en premier ; cliquez sur une ressource pour la lire, ou filtrez par thème.
        </p>

        {/* Filtres */}
        <div className="space-y-3 mb-6">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" /></svg>
              <input type="search" placeholder="Rechercher un mot, un sujet…" value={search}
                onChange={e => setSearch(e.target.value)} aria-label="Rechercher"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500" />
            </div>
            {nbNouveaux > 0 && (
              <button type="button" onClick={() => setNouveautes(v => !v)} aria-pressed={nouveautes}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition ${nouveautes
                  ? 'border-red-300 bg-red-50 text-red-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                Nouveautés seulement ({nbNouveaux})
              </button>
            )}
          </div>
          {themes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filtrer par thème">
              <ChipTheme actif={!domaine} onClick={() => setDomaine('')}>Tous les thèmes</ChipTheme>
              {themes.filter(([d], i) => tousThemes || i < NB_THEMES_VISIBLES || d === domaine).map(([d, n], i) => (
                <ChipTheme key={d} actif={domaine === d} onClick={() => setDomaine(domaine === d ? '' : d)}
                  className={!tousThemes && i >= NB_THEMES_MOBILE && d !== domaine ? 'hidden sm:inline-block' : ''}>
                  {majuscule(d)} <span className={domaine === d ? 'text-gray-300' : 'text-gray-400'}>{n}</span>
                </ChipTheme>
              ))}
              {themes.length > NB_THEMES_MOBILE && (
                <button type="button" onClick={() => setTousThemes(v => !v)}
                  className={`px-2 py-1 text-sm text-blue-700 hover:underline ${themes.length > NB_THEMES_VISIBLES ? '' : 'sm:hidden'}`}>
                  {tousThemes ? 'Moins de thèmes' : 'Autres thèmes…'}
                </button>
              )}
            </div>
          )}
        </div>

        <p className="text-sm text-gray-500 mb-6">
          {filtered.length} ressource{filtered.length > 1 ? 's' : ''}
          {filtresActifs && (
            <> · <button type="button" onClick={effacer} className="text-blue-700 hover:underline">Effacer les filtres</button></>
          )}
        </p>

        {groupes.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-16 text-center text-sm text-gray-500">
            Aucune ressource ne correspond à votre recherche.
          </div>
        ) : groupes.map(g => (
          <section key={g.cle} className="mb-10">
            <h2 className="sticky top-0 z-10 -mx-4 sm:mx-0 mb-4 border-b border-gray-200 bg-gray-50/95 px-4 sm:px-0 py-2 text-sm font-semibold uppercase tracking-wide text-gray-500 backdrop-blur">
              {g.titre}
            </h2>
            {g.cartes.length > 0 && (
              <div className="space-y-3">
                {g.cartes.map(r => <CarteRessource key={r.id} r={r} onLire={setDetailId} />)}
              </div>
            )}
            {g.sujets.length > 0 && (
              <div className={g.cartes.length > 0 ? 'mt-5' : ''}>
                <p className="text-xs font-medium text-gray-500">Sujets notés, sans texte ni lien :</p>
                <ul className="divide-y divide-gray-200">
                  {g.sujets.map(r => <LigneSujet key={r.id} r={r} />)}
                </ul>
              </div>
            )}
          </section>
        ))}
      </main>

      {detailRessource && <PanneauLecture key={detailRessource.id} ressource={detailRessource} onClose={fermer} />}
    </div>
  );
}
